"use client";

import React, { useState, useEffect } from "react";
import { Building2, Layers, ShieldCheck, Tag, FileText, ImageIcon } from "lucide-react";
interface ShowProjectProps {
  id?: number | null;
}
export default function ShowProject({
  id
}: ShowProjectProps) {
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const targetId = React.useMemo(() => {
    if (id) return id;
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const pParam = urlParams.get("p");
      if (pParam && !isNaN(Number(pParam))) return Number(pParam);
    }
    return null;
  }, [id]);
  useEffect(() => {
    if (!targetId) return;
    const fetchProjectDynamic = async () => {
      const is10Web = typeof window !== "undefined" && window.location.hostname.includes("10web.io");
      const apiBase = is10Web ? "https://hents.com.tw" : "";
      const wpUrl = `${apiBase}/?rest_route=/wp/v2/portfolio/${targetId}&_embed=1&_=${Date.now()}`;
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(wpUrl, {
          method: "GET",
          headers: {
            "X-Requested-With": "XMLHttpRequest"
          }
        });
        if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
        const data = await response.json();
        if (data && data.id) {
          setProject(data);
        } else {
          throw new Error(`找不到該作品案場。`);
        }
      } catch (error: any) {
        console.error(" [ShowProject 載入失敗]", error.message);
        // 保底假資料測試，方便直接預覽排版
        setProject({
          id: targetId,
          title: {
            rendered: "栖宇 台南鹽行私宅"
          },
          acf: {
            design_company: "栖宇空間設計",
            flooring_brand: "亨特超耐磨木地板",
            flooring_series: "鄉村系列",
            flooring_model: "HD9337"
          },
          acf_images: {
            image_1: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?q=80&w=1200",
            image_2: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=800",
            image_3: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=800"
          }
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProjectDynamic();
  }, [targetId]);
  if (loading) {
    return <div className="flex flex-col items-center justify-center py-24 space-y-4">
        <div className="size-8 border-4 border-[#4A3728]/10 border-t-[#4A3728] rounded-full animate-spin"></div>
        <p className="text-sm text-[#4A3728] animate-pulse font-serif">專案細節同步中...</p>
      </div>;
  }
  if (error || !project) {
    return <div className="flex flex-col items-center justify-center py-16 text-muted-foreground border border-dashed rounded-[20px] bg-white/30 m-4">
        <p className="font-serif italic text-xs tracking-widest text-[#4A3728]">{error || "無法加載案場目錄"}</p>
      </div>;
  }
  const acf = project.acf || {};
  const title = project.title?.rendered || "未命名案場";
  const fallbackUrl = "https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=1200";
  const specList = [{
    label: "設計",
    value: acf.design_company || "尚未填寫"
  }, {
    label: "品牌",
    value: acf.flooring_brand || "尚未填寫"
  }, {
    label: "系列",
    value: acf.flooring_series || "尚未填寫"
  }, {
    label: "型號",
    value: acf.flooring_model || "尚未填寫"
  }];
  return <section id="show-project-section" className="bg-background animate-in fade-in duration-700 py-6">
      <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
        
        {/*  雜誌感交錯網格佈局 (電腦版分左右 12 欄，精準定位每一個物件) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-12 lg:gap-x-12 items-center">
          
          {/* 1. 完工大圖 1 (最上方滿版橫圖) */}
          <div className="col-span-12 overflow-hidden rounded-sm shadow-sm bg-white">
            <img src={project.acf_images?.image_1 || fallbackUrl} alt="完工實景 1" className="w-full h-auto max-h-[550px] object-cover" />
          </div>

          {/* 2. 完工圖 2 (中間左側豎圖) */}
          <div className="col-span-12 lg:col-span-7 overflow-hidden rounded-sm shadow-sm bg-white">
            <img src={project.acf_images?.image_2 || fallbackUrl} alt="完工實景 2" className="w-full h-auto max-h-[600px] object-cover" />
          </div>

          {/* 3.  核心看點：右側資訊欄 (夾在圖一與圖三中間) */}
          <div className="col-span-12 lg:col-span-5 lg:pl-4 space-y-8 select-none">
            {/* 案場大標題 */}
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#4A3728] tracking-wider leading-tight">
              {title}
            </h2>
            
            {/* 乾淨無框線乾淨清單 (對齊截圖極簡風格) */}
            <div className="space-y-6">
              {specList.map((spec, index) => <div key={index} className="space-y-1 pb-4 border-b border-black/[0.06] last:border-b-0">
                  <p className="text-[11px] font-sans font-bold text-[#b09e94] tracking-widest uppercase">
                    {spec.label}
                  </p>
                  <p className="text-base font-medium text-[#4A3728] tracking-wide">
                    {spec.value}
                  </p>
                </div>)}
            </div>
          </div>

          {/* 4. 完工圖 3 (最下方滿版橫圖) */}
          <div className="col-span-12 overflow-hidden rounded-sm shadow-sm bg-white mt-4 lg:mt-0">
            <img src={project.acf_images?.image_3 || fallbackUrl} alt="完工實景 3" className="w-full h-auto max-h-[550px] object-cover" />
          </div>

        </div>

      </div>
    </section>;
}